import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import { fetchStreakConfig, updateStreakConfig, uploadStreakIcon } from '../services/streakConfigApi';
import Alert from './Alert';
import PermissionGate from './PermissionGate';

const DEFAULT_MILESTONES = [
  { days: 3,  tokens: 15,  emoji: '🔥',     title: '3-day streak'  },
  { days: 5,  tokens: 25,  emoji: '🔥🔥',   title: '5-day streak'  },
  { days: 7,  tokens: 40,  emoji: '🔥🔥🔥', title: '7-day streak'  },
  { days: 10, tokens: 60,  emoji: '⚡',     title: '10-day streak' },
  { days: 15, tokens: 90,  emoji: '⭐',     title: '15-day streak' },
  { days: 20, tokens: 130, emoji: '💎',     title: '20-day streak' },
  { days: 30, tokens: 200, emoji: '👑',     title: '30-day streak' },
];

function emptyMilestone() {
  return { days: '', tokens: '', emoji: '', iconUrl: '', title: '' };
}

function normalizeFromServer(cfg) {
  return {
    milestones: (cfg?.milestones || DEFAULT_MILESTONES).map((m) => ({
      days: String(m.days ?? ''),
      tokens: String(m.tokens ?? ''),
      emoji: m.emoji || '',
      iconUrl: m.iconUrl || '',
      title: m.title || '',
    })),
    holdsPerMonth: String(cfg?.holdsPerMonth ?? 2),
    holdResetMode: cfg?.holdResetMode || 'calendar-month',
    enabled: cfg?.enabled !== false,
  };
}

function validate(form) {
  const errors = [];
  const milestones = [];
  form.milestones.forEach((m, i) => {
    const days = Number.parseInt(m.days, 10);
    const tokens = Number.parseInt(m.tokens, 10);
    if (Number.isNaN(days) || Number.isNaN(tokens)) {
      errors.push(`Row ${i + 1}: days and tokens must be numbers.`);
      return;
    }
    if (days <= 0 || days > 30) errors.push(`Row ${i + 1}: days must be between 1 and 30.`);
    if (tokens < 0) errors.push(`Row ${i + 1}: tokens must be ≥ 0.`);
    milestones.push({
      days,
      tokens,
      emoji: m.emoji.trim(),
      iconUrl: m.iconUrl.trim(),
      title: m.title.trim() || `${days}-day streak`,
    });
  });

  const sorted = [...milestones].sort((a, b) => a.days - b.days);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].days === sorted[i - 1].days) {
      errors.push(`Duplicate milestone at ${sorted[i].days} days.`);
      break;
    }
  }

  const holdsPerMonth = Number.parseInt(form.holdsPerMonth, 10);
  if (Number.isNaN(holdsPerMonth) || holdsPerMonth < 0 || holdsPerMonth > 5) {
    errors.push('Holds per month must be a number between 0 and 5.');
  }

  return {
    errors,
    payload: {
      milestones: sorted,
      holdsPerMonth: Number.isNaN(holdsPerMonth) ? 2 : holdsPerMonth,
      holdResetMode: form.holdResetMode,
      enabled: form.enabled,
    },
  };
}

function StreakConfig() {
  const [form, setForm] = useState(() => normalizeFromServer(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIconIndex, setUploadingIconIndex] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const cfg = await fetchStreakConfig();
      setForm(normalizeFromServer(cfg));
    } catch (err) {
      setAlertMessage(err.message || 'Failed to load streak settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const previewSorted = useMemo(() => {
    return [...form.milestones]
      .map((m) => ({
        days: Number.parseInt(m.days, 10),
        tokens: Number.parseInt(m.tokens, 10),
        emoji: m.emoji,
        iconUrl: m.iconUrl,
        title: m.title,
      }))
      .filter((m) => !Number.isNaN(m.days) && !Number.isNaN(m.tokens))
      .sort((a, b) => a.days - b.days);
  }, [form.milestones]);

  const updateMilestone = (idx, field, value) => {
    setForm((prev) => {
      const next = prev.milestones.slice();
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, milestones: next };
    });
  };

  const removeMilestone = (idx) => {
    setForm((prev) => ({ ...prev, milestones: prev.milestones.filter((_, i) => i !== idx) }));
  };

  const addMilestone = () => {
    setForm((prev) => ({ ...prev, milestones: [...prev.milestones, emptyMilestone()] }));
  };

  const handleIconUpload = async (idx, file) => {
    if (!file) return;
    setUploadingIconIndex(idx);
    try {
      const result = await uploadStreakIcon(file);
      updateMilestone(idx, 'iconUrl', result.iconUrl || '');
      setAlertMessage('Streak icon uploaded. Click Save to persist.');
    } catch (err) {
      setAlertMessage(err.message || 'Failed to upload streak icon.');
    } finally {
      setUploadingIconIndex(null);
    }
  };

  const restoreDefaults = () => {
    setForm(normalizeFromServer(null));
    setAlertMessage('Restored defaults locally. Click Save to persist.');
  };

  const handleSave = async () => {
    const { errors, payload } = validate(form);
    if (errors.length > 0) {
      setAlertMessage(errors.join('\n'));
      return;
    }
    setSaving(true);
    try {
      const saved = await updateStreakConfig(payload);
      setForm(normalizeFromServer(saved));
      setLastSavedAt(new Date());
      setAlertMessage('Streak settings saved.');
    } catch (err) {
      setAlertMessage(err.message || 'Failed to save streak settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="m-auto p-6 md:p-10 max-w-screen-xl">
      {alertMessage && (
        <Alert message={alertMessage} onClose={() => setAlertMessage(null)} />
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Streak Settings</h2>
          <p className="text-gray-500 mt-1">
            Configure streak milestone rewards, monthly Streak Holds, and the streak rewards toggle.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            className="border px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50"
            disabled={loading}
          >
            <ArrowPathIcon className="size-5" />
            Reload
          </button>
          <PermissionGate permission="loyalty.streaks.update"><button
            type="button"
            onClick={handleSave}
            className="bg-teal-500 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60"
            disabled={loading || saving || uploadingIconIndex !== null}
          >
            <CheckIcon className="size-5" />
            {saving ? 'Saving...' : 'Save'}
          </button></PermissionGate>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <div className="loader border-t-4 border-teal-500 border-solid rounded-full w-12 h-12 mx-auto animate-spin"></div>
          <p className="mt-2">Loading streak settings...</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded shadow p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">Milestone Rewards</h3>
                <button
                  type="button"
                  onClick={addMilestone}
                  className="text-teal-500 hover:opacity-70 flex items-center gap-1 text-sm font-medium"
                >
                  <PlusIcon className="size-4" />
                  Add milestone
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Days must be between 1 and 30. Largest tier ≤ 30 — no payouts beyond a 30-day streak.
              </p>

              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="text-gray-500">
                      <th className="py-2 pr-3">Days</th>
                      <th className="py-2 pr-3">Tokens</th>
                      <th className="py-2 pr-3">Icon</th>
                      <th className="py-2 pr-3">Title</th>
                      <th className="py-2 pr-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.milestones.map((m, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="py-2 pr-3">
                          <input
                            type="number"
                            min="1"
                            max="30"
                            value={m.days}
                            onChange={(e) => updateMilestone(idx, 'days', e.target.value)}
                            className="w-20 border px-2 py-1 rounded"
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            type="number"
                            min="0"
                            value={m.tokens}
                            onChange={(e) => updateMilestone(idx, 'tokens', e.target.value)}
                            className="w-24 border px-2 py-1 rounded"
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2 min-w-56">
                            <div className="size-10 border rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                              {m.iconUrl ? (
                                <img src={m.iconUrl} alt="" className="max-h-8 max-w-8 object-contain" />
                              ) : (
                                <span className="text-base">{m.emoji}</span>
                              )}
                            </div>
                            <label className="border px-2 py-1 rounded text-xs cursor-pointer hover:bg-gray-50">
                              {uploadingIconIndex === idx ? 'Uploading...' : 'Upload'}
                              <input
                                type="file"
                                accept="image/svg+xml,image/png,image/webp,image/jpeg"
                                className="hidden"
                                disabled={uploadingIconIndex === idx}
                                onChange={(e) => {
                                  handleIconUpload(idx, e.target.files?.[0]);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                            {m.iconUrl && (
                              <button
                                type="button"
                                onClick={() => updateMilestone(idx, 'iconUrl', '')}
                                className="text-xs text-gray-500 hover:underline"
                              >
                                Clear
                              </button>
                            )}
                            <input
                              type="text"
                              value={m.emoji}
                              onChange={(e) => updateMilestone(idx, 'emoji', e.target.value)}
                              className="w-16 border px-2 py-1 rounded text-base"
                              maxLength={8}
                              placeholder="Emoji"
                              title="Fallback emoji"
                            />
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            type="text"
                            value={m.title}
                            onChange={(e) => updateMilestone(idx, 'title', e.target.value)}
                            className="w-full border px-2 py-1 rounded"
                            placeholder={`${m.days || '?'}-day streak`}
                          />
                        </td>
                        <td className="py-2 pr-3 text-right">
                          <button
                            type="button"
                            onClick={() => removeMilestone(idx)}
                            className="text-red-500 hover:opacity-70"
                            aria-label="Remove milestone"
                          >
                            <TrashIcon className="size-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {form.milestones.length === 0 && (
                      <tr>
                        <td colSpan="5" className="py-6 text-center text-gray-500">
                          No milestones — users will earn streaks but no token rewards.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={restoreDefaults}
                  className="text-sm text-gray-500 hover:underline"
                >
                  Restore default table
                </button>
              </div>
            </div>

            <div className="bg-white rounded shadow p-5">
              <h3 className="text-lg font-bold mb-2">Streak Holds</h3>
              <p className="text-sm text-gray-500 mb-4">
                Holds auto-apply on missed days. When holds run out, the next missed day kills the streak.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Holds per month</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    value={form.holdsPerMonth}
                    onChange={(e) => setForm((p) => ({ ...p, holdsPerMonth: e.target.value }))}
                    className="w-full border px-3 py-2 rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">0 disables holds entirely. Max 5.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Reset mode</label>
                  <select
                    className="w-full border px-3 py-2 rounded"
                    value={form.holdResetMode}
                    onChange={(e) => setForm((p) => ({ ...p, holdResetMode: e.target.value }))}
                  >
                    <option value="calendar-month">Calendar month (resets on the 1st)</option>
                    <option value="30-day-rolling">30-day rolling window</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded shadow p-5">
              <h3 className="text-lg font-bold mb-2">Payouts &amp; Notifications</h3>
              <p className="text-sm text-gray-500 mb-4">
                Master switch for milestone token payouts and streak push notifications. When disabled,
                streaks still increment but no tokens or reminders are sent.
              </p>
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
                  className="size-5 accent-teal-500"
                />
                <span className="font-medium">{form.enabled ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="bg-white rounded shadow p-5">
              <h3 className="text-lg font-bold mb-3">Preview</h3>
              <div className="space-y-2">
                {previewSorted.length === 0 && (
                  <p className="text-sm text-gray-500">Add at least one milestone to preview the table.</p>
                )}
                {previewSorted.map((m) => (
                  <div key={m.days} className="flex items-center justify-between border-b last:border-b-0 py-2">
                    <span className="text-sm">
                      {m.iconUrl ? (
                        <img src={m.iconUrl} alt="" className="mr-2 inline-block size-5 object-contain align-[-4px]" />
                      ) : (
                        <span className="mr-2 text-base">{m.emoji}</span>
                      )}
                      {m.title || `${m.days}-day streak`}
                    </span>
                    <span className="font-semibold text-teal-600">+{m.tokens}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded shadow p-5 text-sm text-gray-600 space-y-2">
              <h3 className="text-base font-bold text-gray-900">How it works</h3>
              <p>Milestones are re-earnable: each fresh streak run can claim each tier once.</p>
              <p>Saving here updates <code className="bg-gray-100 px-1 rounded">config/streaks</code> and is picked up by Cloud Functions within ~5 min.</p>
              {lastSavedAt && (
                <p className="text-xs text-gray-500">Last saved: {lastSavedAt.toLocaleString()}</p>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

export default StreakConfig;
